import { useActivityLogs } from '@/features/customers/hooks/useActivityLogs.ts';
import type { PaymentRetry } from '@/features/paymentRetries';
import { paymentRetriesApi } from '@/features/paymentRetries';
import type { SubscriptionAction } from '@/features/subscriptions';
import { ConfirmModal, DateModal, ReasonModal, WarningModal } from '@/features/subscriptions';
import { Loader } from '@/shared/components/common/Loader';
import { PageLayout } from '@/shared/components/PageLayout';
import { usePermissions } from '@/shared/hooks/usePermissions';
import { useSuccessMessage } from '@/shared/hooks/useSuccessMessage';
import { USER_PERMISSIONS } from '@/shared/types/permissions';
import { cilArrowLeft } from '@coreui/icons';
import CIcon from '@coreui/icons-react';
import {
  CAlert,
  CButton,
  CCard,
  CCardBody,
  CCol,
  CNav,
  CNavItem,
  CNavLink,
  CRow,
} from '@coreui/react';
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useBillingAttempts } from '../../hooks/useBillingAttempts';
import { useCatSubscriptions } from '../../hooks/useCatSubscriptions';
import { useCustomerActions } from '../../hooks/useCustomerActions';
import { useCustomerDetail } from '../../hooks/useCustomerDetail';
import { useCustomerPaymentRetries } from '../../hooks/useCustomerPaymentRetries';
import type { ActivityLog, CatSubscription, UpdateCatInfoPayload } from '../../types';
import { ActivityLogDetailsModal } from '../ActivityLogDetailsModal';
import { ActivityLogList } from '../ActivityLogList';
import { BillingAttemptsTable } from '../BillingAttemptsTable';
import { CatInfoEditModal } from '../CatInfoEditModal';
import { CatSubscriptionDetailsModal } from '../CatSubscriptionDetailsModal';
import { CatSubscriptionsTable } from '../CatSubscriptionsTable';
import { CustomerDetails } from '../CustomerDetails';
import { CustomerPaymentRetriesTable } from '../CustomerPaymentRetriesTable';
import { SubscriptionContract } from '../SubscriptionContract';
import { SubscriptionStatus } from '../SubscriptionStatus';
import type { TabKey } from './types';

const CustomerOverview: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { showSuccess } = useSuccessMessage();

  // Tab state
  const [activeTab, setActiveTab] = useState<TabKey>('activity-log');
  const [loadedTabs, setLoadedTabs] = useState<Set<TabKey>>(() => new Set(['activity-log']));

  // Activity log modal state
  const [selectedActivityLog, setSelectedActivityLog] = useState<ActivityLog | null>(null);
  const [activityModalVisible, setActivityModalVisible] = useState(false);

  // Payment retry cancel state
  const [selectedPaymentRetry, setSelectedPaymentRetry] = useState<PaymentRetry | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  // Cat subscription modal state
  const [selectedCatSubscription, setSelectedCatSubscription] = useState<CatSubscription | null>(
    null,
  );
  const [catModalVisible, setCatModalVisible] = useState(false);
  const [selectedCatForEdit, setSelectedCatForEdit] = useState<CatSubscription | null>(null);
  const [catEditModalVisible, setCatEditModalVisible] = useState(false);
  const [catEditLoading, setCatEditLoading] = useState(false);
  const [catEditError, setCatEditError] = useState<string | null>(null);

  // Permission checks
  const canViewCustomer = hasPermission(USER_PERMISSIONS.CustomersView);
  const canEditCatInfo = hasPermission(USER_PERMISSIONS.CustomersCatInfoEdit);
  const canViewPaymentRetries = hasPermission(USER_PERMISSIONS.CustomersPaymentRetriesView);

  // Data hooks
  const { customer, loading, error, refetch } = useCustomerDetail(id || '');
  const referrerLinkTemplate = import.meta.env.VITE_REFERRER_LINK;
  const referrerLink =
    customer?.referrerCode && referrerLinkTemplate
      ? referrerLinkTemplate.replace('{referrerCode}', customer.referrerCode)
      : null;

  // Activity logs hook - only fetch when tab is loaded
  const {
    activityLogs,
    loading: activityLoading,
    error: activityError,
    page: activityPage,
    perPage: activityPerPage,
    total: activityTotal,
    totalPages: activityTotalPages,
    category: activityCategory,
    level: activityLevel,
    handlePageChange: handleActivityPageChange,
    handlePerPageChange: handleActivityPerPageChange,
    handleCategoryChange: handleActivityCategoryChange,
    handleLevelChange: handleActivityLevelChange,
  } = useActivityLogs(loadedTabs.has('activity-log') ? id || '' : '');

  // Payment retries hook - only fetch when tab is loaded
  const {
    paymentRetries,
    loading: paymentRetriesLoading,
    error: paymentRetriesError,
    page: paymentRetriesPage,
    perPage: paymentRetriesPerPage,
    total: paymentRetriesTotal,
    totalPages: paymentRetriesTotalPages,
    handlePageChange: handlePaymentRetriesPageChange,
    handlePerPageChange: handlePaymentRetriesPerPageChange,
    refetch: refetchPaymentRetries,
  } = useCustomerPaymentRetries(loadedTabs.has('payment-retries') ? id || '' : '');

  // Billing attempts hook - only fetch when tab is loaded
  const {
    billingAttempts,
    loading: billingLoading,
    error: billingError,
    page,
    perPage,
    total,
    totalPages,
    handlePageChange,
    handlePerPageChange,
  } = useBillingAttempts(loadedTabs.has('billing-attempts') ? id || '' : '');

  // Cat subscriptions hook - only fetch when tab is loaded
  const {
    catSubscriptions,
    loading: catSubscriptionsLoading,
    error: catSubscriptionsError,
    page: catSubscriptionsPage,
    perPage: catSubscriptionsPerPage,
    total: catSubscriptionsTotal,
    totalPages: catSubscriptionsTotalPages,
    handlePageChange: handleCatSubscriptionsPageChange,
    handlePerPageChange: handleCatSubscriptionsPerPageChange,
    updateCatInfo,
  } = useCatSubscriptions(loadedTabs.has('cat-subscriptions') ? id || '' : '');

  // Actions hook
  const {
    confirmAction,
    reasonModalAction,
    showDateModal,
    showShipNowWarning,
    showChangeDateWarning,
    actionError,
    successMessage,
    handleAction,
    handleConfirmAction,
    handleReasonConfirm,
    handleDateConfirm,
    handleWarningYes,
    handleWarningNo,
    clearError,
    clearSuccessMessage,
    showPageSuccess,
    isActionLoading,
    setConfirmAction,
    setReasonModalAction,
    setShowDateModal,
  } = useCustomerActions(refetch);

  // Tab change handler
  const handleTabChange = (tab: TabKey) => {
    setActiveTab(tab);
    if (!loadedTabs.has(tab)) {
      setLoadedTabs((prev) => new Set([...prev, tab]));
    }
  };

  // Activity log modal handlers
  const handleViewActivityDetails = (log: ActivityLog) => {
    setSelectedActivityLog(log);
    setActivityModalVisible(true);
  };

  const handleCloseActivityModal = () => {
    setActivityModalVisible(false);
    setSelectedActivityLog(null);
  };

  // Payment retry cancel handlers
  const handleCancelPaymentRetry = (paymentRetry: PaymentRetry) => {
    setSelectedPaymentRetry(paymentRetry);
    setShowCancelModal(true);
    setCancelError(null);
  };

  const handleConfirmCancelPaymentRetry = async () => {
    if (!selectedPaymentRetry) return;

    setIsCancelling(true);
    setCancelError(null);

    try {
      await paymentRetriesApi.update(selectedPaymentRetry.id);
      await refetchPaymentRetries();
      showSuccess('Payment retry cancelled successfully');
      setShowCancelModal(false);
      setSelectedPaymentRetry(null);
    } catch (error) {
      setCancelError(error instanceof Error ? error.message : 'Failed to cancel payment retry');
    } finally {
      setIsCancelling(false);
    }
  };

  const handleCancelCancelModal = () => {
    setShowCancelModal(false);
    setSelectedPaymentRetry(null);
    setCancelError(null);
  };

  // Cat subscription modal handlers
  const handleViewCatDetails = (catSubscription: CatSubscription) => {
    setSelectedCatSubscription(catSubscription);
    setCatModalVisible(true);
  };

  const handleCloseCatModal = () => {
    setCatModalVisible(false);
    setSelectedCatSubscription(null);
  };

  const handleEditCatInfo = (catSubscription: CatSubscription) => {
    setSelectedCatForEdit(catSubscription);
    setCatEditError(null);
    setCatEditModalVisible(true);
  };

  const handleCloseCatEditModal = () => {
    setCatEditModalVisible(false);
    setSelectedCatForEdit(null);
    setCatEditError(null);
  };

  const handleSaveCatInfo = async (data: UpdateCatInfoPayload) => {
    if (!selectedCatForEdit) {
      return;
    }

    setCatEditLoading(true);
    setCatEditError(null);

    try {
      await updateCatInfo(selectedCatForEdit.id, data);
      handleCloseCatEditModal();
      showSuccess('Cat information updated successfully');
    } catch (error) {
      setCatEditError(error instanceof Error ? error.message : 'Failed to update cat information');
    } finally {
      setCatEditLoading(false);
    }
  };

  if (!canViewCustomer) {
    return (
      <CCard>
        <CCardBody>
          <CAlert color="danger">You don't have permission to view customer details</CAlert>
          <CButton
            color="primary"
            onClick={() => void navigate(-1)}
            className="d-flex align-items-center"
          >
            <CIcon icon={cilArrowLeft} className="me-2" />
            Back to List
          </CButton>
        </CCardBody>
      </CCard>
    );
  }

  if (loading) {
    return <Loader isLoading={true} />;
  }

  if (error) {
    return (
      <CCard>
        <CCardBody>
          <CAlert color="danger">{error}</CAlert>
          <CButton
            color="primary"
            onClick={() => void navigate(-1)}
            className="d-flex align-items-center"
          >
            <CIcon icon={cilArrowLeft} className="me-2" />
            Back to Search
          </CButton>
        </CCardBody>
      </CCard>
    );
  }

  const customBreadcrumbLabels = {
    dashboard: '',
    ...(id ? { [id]: 'View' } : {}),
  };

  if (!customer) {
    return (
      <PageLayout customBreadcrumbLabels={customBreadcrumbLabels}>
        <CCard>
          <CCardBody>
            <CAlert color="info">Customer not found</CAlert>
            <CButton
              color="primary"
              onClick={() => void navigate(-1)}
              className="d-flex align-items-center"
            >
              <CIcon icon={cilArrowLeft} className="me-2" />
              Back to Search
            </CButton>
          </CCardBody>
        </CCard>
      </PageLayout>
    );
  }

  return (
    <PageLayout customBreadcrumbLabels={customBreadcrumbLabels}>
      {/* Back Button */}
      <CRow className="mb-3">
        <CCol>
          <CButton
            color="outline-primary"
            onClick={() => void navigate(-1)}
            className="d-flex align-items-center"
          >
            <CIcon icon={cilArrowLeft} className="me-2" />
            Back to Search
          </CButton>
        </CCol>
      </CRow>

      {/* Success/Error Messages */}
      {successMessage && (
        <CAlert color="success" dismissible onClose={clearSuccessMessage} className="mb-3">
          {successMessage}
        </CAlert>
      )}
      {actionError && (
        <CAlert color="danger" dismissible onClose={clearError} className="mb-3">
          {actionError}
        </CAlert>
      )}
      {cancelError && (
        <CAlert color="danger" dismissible onClose={() => setCancelError(null)} className="mb-3">
          {cancelError}
        </CAlert>
      )}

      {/* Customer Details & Subscription Status */}
      <CCard className="mb-4">
        <CCardBody>
          <CRow>
            <CCol md={6}>
              <CustomerDetails
                customer={customer}
                onRefetch={refetch}
                onShowSuccess={showPageSuccess}
              />
            </CCol>
            <CCol md={6}>
              {customer.subscriptionPlan && (
                <SubscriptionStatus
                  subscription={customer.subscriptionPlan}
                  shopifyCustomerId={customer.shopifyCustomerId}
                  isActionLoading={isActionLoading}
                  onAction={(action: SubscriptionAction) =>
                    void handleAction(action, customer.shopifyCustomerId)
                  }
                />
              )}
              <div>
                <div className="mb-3">
                  <h3>RAF Status</h3>
                </div>
                {referrerLink && (
                  <div className="mb-3">
                    <strong>Referrer link:</strong>{' '}
                    <a href={referrerLink} target="_blank" rel="noopener noreferrer">
                      {referrerLink}
                    </a>
                  </div>
                )}
                <div className="mb-3">
                  <strong>Unused earned rewards:</strong> {customer.availableReferrerRewardsCount}
                </div>
              </div>
            </CCol>
          </CRow>
        </CCardBody>
      </CCard>

      {/* Tabs Card */}
      <CCard>
        <CCardBody>
          {/* Tab Navigation */}
          <CNav variant="tabs" role="tablist" className="border-bottom">
            <CNavItem>
              <CNavLink
                active={activeTab === 'activity-log'}
                onClick={() => handleTabChange('activity-log')}
                style={{ cursor: 'pointer' }}
              >
                Activity Log
              </CNavLink>
            </CNavItem>
            {canViewPaymentRetries && (
              <CNavItem>
                <CNavLink
                  active={activeTab === 'payment-retries'}
                  onClick={() => handleTabChange('payment-retries')}
                  style={{ cursor: 'pointer' }}
                >
                  Payment Retries
                </CNavLink>
              </CNavItem>
            )}
            <CNavItem>
              <CNavLink
                active={activeTab === 'billing-attempts'}
                onClick={() => handleTabChange('billing-attempts')}
                style={{ cursor: 'pointer' }}
              >
                Billing Attempts
              </CNavLink>
            </CNavItem>
            <CNavItem>
              <CNavLink
                active={activeTab === 'cat-subscriptions'}
                onClick={() => handleTabChange('cat-subscriptions')}
                style={{ cursor: 'pointer' }}
              >
                Cat Info
              </CNavLink>
            </CNavItem>
            <CNavItem>
              <CNavLink
                active={activeTab === 'subscription-contract'}
                onClick={() => handleTabChange('subscription-contract')}
                style={{ cursor: 'pointer' }}
              >
                Subscription Contract
              </CNavLink>
            </CNavItem>
          </CNav>

          {/* Tab Content */}
          <div className="pt-3">
            {activeTab === 'activity-log' && (
              <ActivityLogList
                logs={activityLogs}
                loading={activityLoading}
                error={activityError}
                page={activityPage}
                perPage={activityPerPage}
                total={activityTotal}
                totalPages={activityTotalPages}
                category={activityCategory}
                level={activityLevel}
                onPageChange={handleActivityPageChange}
                onPerPageChange={handleActivityPerPageChange}
                onCategoryChange={handleActivityCategoryChange}
                onLevelChange={handleActivityLevelChange}
                onViewDetails={handleViewActivityDetails}
              />
            )}

            {canViewPaymentRetries && activeTab === 'payment-retries' && (
              <CustomerPaymentRetriesTable
                paymentRetries={paymentRetries}
                loading={paymentRetriesLoading}
                error={paymentRetriesError}
                page={paymentRetriesPage}
                perPage={paymentRetriesPerPage}
                total={paymentRetriesTotal}
                totalPages={paymentRetriesTotalPages}
                onPageChange={handlePaymentRetriesPageChange}
                onPerPageChange={handlePaymentRetriesPerPageChange}
                onCancel={handleCancelPaymentRetry}
              />
            )}

            {activeTab === 'billing-attempts' && (
              <BillingAttemptsTable
                billingAttempts={billingAttempts}
                loading={billingLoading}
                error={billingError}
                page={page}
                perPage={perPage}
                total={total}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                onPerPageChange={handlePerPageChange}
              />
            )}

            {activeTab === 'cat-subscriptions' && (
              <CatSubscriptionsTable
                catSubscriptions={catSubscriptions}
                loading={catSubscriptionsLoading}
                error={catSubscriptionsError}
                page={catSubscriptionsPage}
                perPage={catSubscriptionsPerPage}
                total={catSubscriptionsTotal}
                totalPages={catSubscriptionsTotalPages}
                onPageChange={handleCatSubscriptionsPageChange}
                onPerPageChange={handleCatSubscriptionsPerPageChange}
                onView={handleViewCatDetails}
                onEdit={canEditCatInfo ? handleEditCatInfo : undefined}
              />
            )}

            {activeTab === 'subscription-contract' && (
              <SubscriptionContract shopifyCustomerId={id || ''} />
            )}
          </div>
        </CCardBody>
      </CCard>

      {/* Action Modals */}
      {showShipNowWarning && (
        <WarningModal
          visible={true}
          title="Ship Now Warning"
          message="An order was dispatched to this customer within 24 hours. Do you really want to send another one?"
          loading={isActionLoading(customer?.shopifyCustomerId || 0)}
          onYes={(...args: Parameters<typeof handleWarningYes>) => void handleWarningYes(...args)}
          onNo={handleWarningNo}
        />
      )}

      {showChangeDateWarning && (
        <WarningModal
          visible={true}
          title="Change Date Warning"
          message="An order was dispatched to this customer within 24 hours. Do you really want to schedule another one to be sent so soon?"
          loading={isActionLoading(customer?.shopifyCustomerId || 0)}
          onYes={(...args: Parameters<typeof handleWarningYes>) => void handleWarningYes(...args)}
          onNo={handleWarningNo}
        />
      )}

      {confirmAction && (
        <ConfirmModal
          visible={true}
          title={confirmAction.title}
          message={confirmAction.message}
          confirmText={confirmAction.confirmText}
          confirmColor={confirmAction.confirmColor}
          loading={isActionLoading(customer?.shopifyCustomerId || 0)}
          onConfirm={() => void handleConfirmAction(customer.shopifyCustomerId)}
          onCancel={() => setConfirmAction(null)}
        />
      )}

      {reasonModalAction && (
        <ReasonModal
          visible={true}
          action={reasonModalAction}
          loading={isActionLoading(customer?.shopifyCustomerId || 0)}
          onConfirm={({ reasonId, comment }) =>
            void handleReasonConfirm(customer.shopifyCustomerId, reasonId.toString(), comment)
          }
          onCancel={() => setReasonModalAction(null)}
        />
      )}

      {showDateModal && (
        <DateModal
          visible={true}
          loading={isActionLoading(customer?.shopifyCustomerId || 0)}
          currentDate={customer?.subscriptionPlan?.nextDispatchDate}
          onConfirm={(date) => void handleDateConfirm(customer.shopifyCustomerId, date)}
          onCancel={() => setShowDateModal(false)}
        />
      )}

      {/* Activity Log Details Modal */}
      <ActivityLogDetailsModal
        log={selectedActivityLog}
        visible={activityModalVisible}
        onClose={handleCloseActivityModal}
      />

      {/* Cancel Payment Retry Modal */}
      {showCancelModal && (
        <ConfirmModal
          visible={true}
          title="Cancel Payment Retry"
          message="Are you sure you want to cancel this payment retry? This action cannot be undone."
          confirmText="Cancel Payment Retry"
          confirmColor="danger"
          loading={isCancelling}
          onConfirm={(...args: Parameters<typeof handleConfirmCancelPaymentRetry>) =>
            void handleConfirmCancelPaymentRetry(...args)
          }
          onCancel={handleCancelCancelModal}
        />
      )}

      {/* Cat Subscription Details Modal */}
      <CatSubscriptionDetailsModal
        catSubscription={selectedCatSubscription}
        visible={catModalVisible}
        onClose={handleCloseCatModal}
      />
      <CatInfoEditModal
        catSubscription={selectedCatForEdit}
        visible={catEditModalVisible}
        loading={catEditLoading}
        error={catEditError}
        onSave={(...args: Parameters<typeof handleSaveCatInfo>) => void handleSaveCatInfo(...args)}
        onClose={handleCloseCatEditModal}
      />
    </PageLayout>
  );
};

export { CustomerOverview };
